'use client';

import React, { useEffect, useRef } from 'react';

/**
 * A sphere drawn as a rotating cloud of points.
 *
 * Canvas 2D with the projection done by hand rather than three.js. The whole
 * effect is a few hundred points, a rotation matrix and a perspective divide —
 * pulling in a WebGL engine for that would add roughly 600 KB to a marketing
 * page whose entire visual language is hairlines and monospace labels.
 *
 * Points are placed by the Fibonacci lattice rather than by nesting latitude
 * and longitude loops. Lat/long bunches points at the poles and leaves the
 * equator sparse, which reads as a mistake at exactly the moment the sphere is
 * meant to look precise; the lattice spaces them almost evenly over the
 * surface.
 *
 * Depth does the rest of the work: points further away are drawn smaller and
 * fainter, so the volume is legible without a single line being drawn between
 * them.
 */

type Props = {
  /** Points on the surface. Below ~2000 the cloud reads as a flat disc of
   *  dots rather than a volume — the rim has to be dense enough for the
   *  silhouette to close. */
  count?: number;
  /** Seconds per full turn. Slow on purpose — it should be felt, not watched. */
  secondsPerTurn?: number;
  className?: string;
};

export default function ParticleSphere({
  count = 5600,
  secondsPerTurn = 48,
  className = '',
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* Advection. STEPS is how far along the flow each point is carried, and
       it is the dial that matters: at one step the points have barely moved
       and the surface is still the even lattice it started as; by nine the
       flow has swept them into tight ribbons and stripped the regions between
       almost bare, which is the contrast the reference has. H is the step size
       and K the flow's spatial frequency — low, so the structure is a few
       large sweeps rather than a uniform froth. */
    const STEPS = 9;
    const H = 0.115;
    const K = 2.2;
    /* Only outliers are pulled in, so the outline is drawn by the sheets
       reaching the boundary rather than by a membrane sitting on it. A high
       boost puts most of the cloud on the hull at once, which is a shell by
       another name. */
    const BOOST = 1.12;

    // Scratch for the advected positions. Two passes are needed — one to move
    // the points and find where the cloud has drifted to, one to draw — and a
    // per-frame array of this size would keep the collector busy.
    const adv = new Float32Array(count * 3);

    // Every dot the same size and the same opacity — see the draw loop.
    const DOT = 0.5;
    const DOT2 = DOT * 2;
    const DOT_ALPHA = 0.38;

    // Colour buckets, and a flat scratch buffer for their screen positions.
    // Allocated once: a per-frame array of nine thousand pairs would hand the
    // garbage collector a job sixty times a second.
    const BUCKETS = 10;
    const BUCKET_CAP = Math.ceil(count * 1.2);
    const bucketXY = new Float32Array(BUCKETS * BUCKET_CAP * 2);
    const bucketLen = new Int32Array(BUCKETS);
    // Camera distance in radii. Near enough for the near face to read as
    // nearer, far enough not to fish-eye the silhouette.
    const CAM = 3.4;

    // ── Point set ────────────────────────────────────────────────────────────
    // Golden-angle spiral. The first version used rings of latitude, because
    // ordered rows make a displacement legible the way contour lines show a
    // hill — but that was when the folds had to be read from a few hundred
    // shaded points. At this density the structure comes from where the
    // projected points pile up, so the rows are no longer doing that job, and
    // what they leave behind is a pole at each end: the rings hold fewest
    // points there, so a deep fold collapsed them into a visible spike.
    //
    // A spiral has no poles to collapse, spaces points almost evenly over the
    // surface, and its interference with itself through the far side is
    // exactly the moiré the reference has.
    const GOLDEN = Math.PI * (3 - Math.sqrt(5));
    /* Two populations, which is what the reference actually is.

       Blending every point back toward the sphere gave the body its shape and
       took the tearing with it; letting every point run free gave the tearing
       and lost the sphere. Neither is a compromise worth making, because the
       picture has both at once: a clean thin shell tracing a circle, and torn
       sheets threading the volume behind it.

       So a third of the points barely move — they are the shell and the rim —
       and the rest are carried the whole way by the flow. */
    const pts: { theta: number; phi: number }[] = [];
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2; // -1 … 1
      pts.push({ theta: Math.acos(Math.max(-1, Math.min(1, y))), phi: GOLDEN * i });
    }

    let raf = 0;
    let running = true;
    let angle = 0;
    let last = performance.now();

    // Colours come from the stylesheet so the sphere follows the theme and the
    // accent without a second source of truth.
    const parseRgb = (
      value: string,
      fallback: [number, number, number]
    ): [number, number, number] => {
      const v = value.trim();
      if (/^#[0-9a-f]{6}$/i.test(v)) {
        return [
          parseInt(v.slice(1, 3), 16),
          parseInt(v.slice(3, 5), 16),
          parseInt(v.slice(5, 7), 16),
        ];
      }
      const nums = v.match(/\d+/g);
      if (nums && nums.length >= 3) return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
      return fallback;
    };

    const readColours = () => {
      const cs = getComputedStyle(document.documentElement);
      return {
        // The rim colour. --m-ink is near-black in light and near-white in
        // dark, so the sphere's edge dissolves into whichever page it is on.
        ink: parseRgb(cs.getPropertyValue('--m-ink-rgb'), [17, 17, 17]),
        // The core. A lighter orange than the interface accent — at #c2410c
        // the core read as brown against a dark ground.
        accent: parseRgb(cs.getPropertyValue('--m-sphere-core') || '#fbaa6a', [251, 170, 106]),
      };
    };
    let { ink, accent } = readColours();

    const themeObserver = new MutationObserver(() => {
      ({ ink, accent } = readColours());
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    const draw = (now: number) => {
      const dt = Math.min(64, now - last);
      last = now;
      if (!reduced.matches) angle += (dt / 1000) * ((Math.PI * 2) / secondsPerTurn);

      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
        canvas.width = cw * dpr;
        canvas.height = ch * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, ch);

      const cx = cw / 2;
      const cy = ch / 2;
      // No headroom needed: the wave only dents inward, so the hull is the
      // widest the body ever gets.
      const radius = Math.min(cw, ch) * 0.46;

      // A fixed tilt, so the axis of rotation is visible as an axis rather than
      // the sphere appearing to spin flat.
      const TILT = -0.42;
      const sinT = Math.sin(TILT);
      const cosT = Math.cos(TILT);
      const sinA = Math.sin(angle);
      const cosA = Math.cos(angle);

      // Wave phase advances even when the body's rotation is frozen for
      // reduced motion — it is the surface that should be still, not just the
      // spin, so both are gated together above.
      const t = angle * 2.4;

      /* Points are bucketed by colour and drawn in one pass per bucket.
         Setting fillStyle is the expensive call in Canvas 2D — doing it once
         per point is what caps this kind of thing at a couple of thousand
         dots. Ten buckets means ten state changes a frame instead of nine
         thousand, which is what pays for the density the look needs. */
      for (let bkt = 0; bkt < BUCKETS; bkt++) bucketLen[bkt] = 0;

      /* ── Pass 1: carry every point across the surface ──────────────────

         The points never leave the sphere. Every step of the flow is followed
         by a projection back onto it, which keeps only the tangential part of
         the velocity — so the body's outline is a perfect circle in every
         frame, by construction rather than by correction.

         Everything else in the picture is density. The flow is divergence-free
         in space but its restriction to a sphere is not, so it sweeps points
         together into tight ribbons and strips other regions almost bare. The
         sheets that appear to dive through the middle are the far side of the
         surface, seen through the gaps the flow has opened in the near side.

         This replaces three failed attempts at the same goal. Letting the flow
         carry points off the sphere and then trying to make the cloud round
         again — by anchoring a third of them, by scaling each axis to its own
         spread, by rescaling the radius — gave aspects of 1.293, 1.471 and
         1.774 respectively. The cloud is genuinely anisotropic after five
         steps of chaotic advection, and no global rescale of an anisotropic
         cloud makes its silhouette circular. Not letting it leave in the first
         place is the answer. */
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const sinTh = Math.sin(p.theta);
        let px = sinTh * Math.cos(p.phi);
        let py = Math.cos(p.theta);
        let pz = sinTh * Math.sin(p.phi);

        for (let step = 0; step < STEPS; step++) {
          const vx = Math.sin(K * pz + t) + Math.cos(K * py + t * 0.7);
          const vy = Math.sin(K * px + t * 0.83) + Math.cos(K * pz + t * 0.51);
          const vz = Math.sin(K * py + t * 0.62) + Math.cos(K * px + t * 0.94);
          px += H * vx;
          py += H * vy;
          pz += H * vz;
          // Straight back onto the sphere after every step, which keeps only
          // the tangential part of the flow. See the note above the pass.
          const l = Math.sqrt(px * px + py * py + pz * pz) || 1;
          px /= l;
          py /= l;
          pz /= l;
        }

        const o = i * 3;
        adv[o] = px;
        adv[o + 1] = py;
        adv[o + 2] = pz;
      }

      /* The flow has a net drift over the ball, so after five steps the whole
         cloud has translated — which is what turned the sphere into a bowl
         when the anchored points were removed (measured at an aspect of
         1.293). Subtracting the centroid removes the translation and leaves
         the stirring, so the body stays centred and round without anything
         being held in place. */
      /* Pass 2: project and bucket by colour.
         Setting fillStyle is the expensive call in Canvas 2D — once per point
         caps this at a couple of thousand dots. Ten buckets is ten state
         changes a frame, which is what pays for the density. */
      for (let bkt = 0; bkt < BUCKETS; bkt++) bucketLen[bkt] = 0;

      for (let i = 0; i < pts.length; i++) {
        const o = i * 3;
        const px = adv[o];
        const py = adv[o + 1];
        const pz = adv[o + 2];

        // Rotate about Y, then tilt about X.
        const x1 = px * cosA - pz * sinA;
        const z1 = px * sinA + pz * cosA;
        const y2 = py * cosT - z1 * sinT;
        const z2 = py * sinT + z1 * cosT;

        // Perspective divide. z2 grows toward the viewer, so distance from the
        // camera is CAM - z2.
        const scale = CAM / (CAM - z2);
        const sx = cx + x1 * radius * scale;
        const sy = cy + y2 * radius * scale;

        // Colour runs centre-out: the core carries the accent and the rim
        // gives it up to the page's own ink.
        const dx = (sx - cx) / radius;
        const dy = (sy - cy) / radius;
        const rimT = Math.pow(Math.min(1, dx * dx + dy * dy), 0.35);
        const bkt = Math.min(BUCKETS - 1, (rimT * BUCKETS) | 0);

        const slot = bucketLen[bkt];
        if (slot < BUCKET_CAP) {
          const base = bkt * BUCKET_CAP * 2 + slot * 2;
          bucketXY[base] = sx;
          bucketXY[base + 1] = sy;
          bucketLen[bkt] = slot + 1;
        }
      }

      /* One size, one opacity, for every point regardless of depth.
         This is the whole difference between the reference's airy sphere and
         the solid ball this was drawing before. Fading and shrinking the far
         side hides it completely, so all you see is a lit front face — a
         solid. Drawing front and back identically lets the far surface show
         through the near one, and the brightness then comes from where the
         projected points pile up: dense and bright at the rim where the
         surface turns edge-on, open and dark across the middle. The moiré
         that gives it its texture is the two hemispheres interfering, and it
         only exists if both are actually drawn. */
      for (let bkt = 0; bkt < BUCKETS; bkt++) {
        const n = bucketLen[bkt];
        if (!n) continue;
        const mix = (bkt + 0.5) / BUCKETS;
        const cr = (accent[0] + (ink[0] - accent[0]) * mix) | 0;
        const cg = (accent[1] + (ink[1] - accent[1]) * mix) | 0;
        const cb = (accent[2] + (ink[2] - accent[2]) * mix) | 0;
        ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${DOT_ALPHA})`;
        const base = bkt * BUCKET_CAP * 2;
        for (let j = 0; j < n; j++) {
          // fillRect, not arc: at this size a square and a circle are the same
          // two pixels, and arc costs a path per dot.
          ctx.fillRect(bucketXY[base + j * 2] - DOT, bucketXY[base + j * 2 + 1] - DOT, DOT2, DOT2);
        }
      }

      ctx.globalAlpha = 1;

      if (running) raf = requestAnimationFrame(draw);
    };

    // Paint one frame immediately so the sphere is present before any of the
    // conditions below decide whether it should animate.
    raf = requestAnimationFrame(draw);

    // Off-screen or reduced-motion: hold the frame rather than burning a
    // repaint every 16ms for something nobody is looking at.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !running) {
          running = true;
          last = performance.now();
          raf = requestAnimationFrame(draw);
        } else if (!entry.isIntersecting && running) {
          running = false;
          cancelAnimationFrame(raf);
        }
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      io.disconnect();
      themeObserver.disconnect();
    };
  }, [count, secondsPerTurn]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label="Rotating point-cloud sphere"
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}
